import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogTrigger } from '../../../../../components/ui/dialog'
import { AnimatedHeightSwitch } from '../../../../../components/ui/animated-height'
import { useWidget } from '../../../../../providers/widget'
import { ExploreWidgetConfig } from '../../../config'
import { useExplorer } from '../../../providers'
import { useExplorerCss } from '../../../hooks/useExplorerCss'
import { curateOptions, priceFieldForMode, useFetchExplorerMeta } from '../../../data'
import { useNameFinder } from './useNameFinder'
import { usePropertyNavigate } from './propertyNav'
import { usePriceDistribution } from './usePriceDistribution'
import PriceHistogramSlider from './PriceHistogramSlider'
import FieldClear from './FieldClear'
import { useSbpLabels } from './labels'
import { useSbpCompact, SrLabel, FiltersIcon } from './compact'
import { useSbpPopover } from './useSbpPopover'
import {
  countActiveWhatFilters, csvIds, idsToCsv, whatFieldLabel, whatPlaceholder, WhatSectionVisibility,
} from './whatFilters'

type Tab = 'describe' | 'filter' | 'byname'

const searchGlyph = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

/**
 * WHAT popover (#870 slice 5, toolkit: `search-bar.php:734-905`). The pro
 * variant's Filters field opens a tabbed surface — Filter, plus Describe and
 * By-name when they are available — instead of the legacy Apply/Clear modal.
 *
 * LIVE-APPLY (Eli's #870 decision 1): there is no Apply button. Every control
 * writes its filter the moment it changes and the field pill becomes
 * "N filters", so the results behind the popover refine as the visitor works.
 * Nothing is drafted, which also means nothing can be silently discarded by
 * closing the popover.
 *
 * Both extra tabs ship dark:
 *   • Describe is gated on `searchbar.what.aiConcierge`, which is false until
 *     feeds can run the AI Concierge (#870 decision 5).
 *   • By name is ON by default but still hidden until `useNameFinder`'s probe
 *     succeeds, and its endpoint is central#2885 — filed, unbuilt.
 */
export default function WhatPopover({ popover = false, sheet = false, sheetActive = false, onSheetActivate, advanceSignal = 0 }: { popover?: boolean; sheet?: boolean; sheetActive?: boolean; onSheetActivate?: () => void; advanceSignal?: number } = {}) {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const cssString = useExplorerCss()
  const sb = options.searchbar
  const labels = useSbpLabels()
  const compactBar = useSbpCompact()
  const [open, setOpen] = useState(false)
  // Sheet stages (Eli r5) render the panes INLINE in the sheet accordion with
  // no dialog; the accordion's active flag is their "open" for the probe
  // effects below. Live-apply semantics are already this component's contract.
  const effectiveOpen = sheet ? sheetActive : open
  // Desktop pro instances anchor under the bar's right half like Who
  // (#870 decision 22); mobile-sheet stages and unmeasurable bars keep the
  // centred modal. Same double-gate as the other fields.
  const { triggerRef, measureNow, anchored, wrapperStyle } = useSbpPopover(popover, 'right', open)

  // Chained popovers (#923): the bar coordinator bumps this to hand the
  // hero bar to this field (guests Done → WHAT, or bedrooms Done → WHAT).
  useEffect(() => {
    if (!advanceSignal) return
    measureNow()
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSignal])

  // Describe text lives on the field (not the pane) so it survives the popover
  // closing. It has no filter contract yet — the concierge submission wiring
  // lands with the feed-side work that turns `what.aiConcierge` on.
  const [describe, setDescribe] = useState('')

  const priceField = priceFieldForMode(options.priceDisplay.noDates)
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceField)
  const meta = metaData?.result
  const priceMin = meta?.priceRange?.min ?? 0
  const priceMax = meta?.priceRange?.max ?? 10000

  const curatedTypes = useMemo(
    () => curateOptions(meta?.taxonomies?.types ?? [], options.filterOptions?.types?.split(',').filter(Boolean)),
    [meta?.taxonomies?.types, options.filterOptions]
  )
  // Amenities follow the legacy pane's WP-parity rule (#556): only explicitly
  // selected amenities render, and an empty selection renders none at all.
  const curatedAmenities = useMemo(() => {
    const selected = options.filterOptions?.amenities?.split(',').filter(Boolean) ?? []
    if (selected.length === 0) return []
    return curateOptions(meta?.taxonomies?.amenities ?? [], selected)
  }, [meta?.taxonomies?.amenities, options.filterOptions])

  // Section visibility — same admin settings the legacy pane honors. This
  // answers "which controls does the pane render", which is NOT the same
  // question as "which filters are still live".
  // The array is ORDERED: the dashboard's drag-to-reorder control saves the
  // section sequence and the pane renders it verbatim (r7 #936). The
  // fallback mirrors the schema default — module order, keyword (Filter by
  // Name) opt-in. Deduped defensively: a hand-saved duplicate would render
  // twice with colliding keys.
  const enabledFilters = Array.from(new Set(options.additionalFilters ?? ['rooms', 'types', 'price', 'amenities']))
  // Room steppers are the only dimension the request path strips when hidden
  // (`stripHiddenRoomFilters`, data.ts) — and it strips on the two
  // show*Filter toggles ALONE, with no `additionalFilters` term. The count
  // has to be gated on exactly that, or a hidden-but-still-sent dimension
  // becomes an invisible active filter with no control left to clear it.
  const bedroomsLive = options.showBedroomsFilter !== false
  const bathroomsLive = options.showBathroomsFilter !== false
  const visibility: WhatSectionVisibility = {
    keyword: enabledFilters.includes('keyword'),
    price: enabledFilters.includes('price'),
    bedrooms: bedroomsLive && enabledFilters.includes('rooms'),
    bathrooms: bathroomsLive && enabledFilters.includes('rooms'),
    types: enabledFilters.includes('types') && curatedTypes.length > 0,
    amenities: enabledFilters.includes('amenities') && curatedAmenities.length > 0,
  }

  const vf = state.visitorFilters
  const activeCount = countActiveWhatFilters(vf, { bedrooms: bedroomsLive, bathrooms: bathroomsLive })
  const placeholder = whatPlaceholder(sb.what.aiConcierge)
  const fieldValue = describe.trim() ? describe.trim() : whatFieldLabel(activeCount, placeholder)
  const fieldHasValue = Boolean(describe.trim()) || activeCount > 0

  // ── Live-apply writes ───────────────────────────────────────────────────
  const selectedTypes = csvIds(vf.types)
  const amenitiesKey = options.requireAllAmenities === true ? 'amenities_and' : 'amenities'
  const selectedAmenities = csvIds(vf.amenities ?? vf.amenities_and)

  const toggleType = (id: string) => {
    const next = selectedTypes.includes(id) ? selectedTypes.filter((t) => t !== id) : [...selectedTypes, id]
    setFilter({ types: idsToCsv(next) })
  }

  const toggleAmenity = (id: string) => {
    const next = selectedAmenities.includes(id) ? selectedAmenities.filter((a) => a !== id) : [...selectedAmenities, id]
    // Write BOTH keys: the match-all setting can flip between saves, and a
    // stale value left under the other key would keep filtering invisibly.
    setFilter({ amenities: undefined, amenities_and: undefined, [amenitiesKey]: idsToCsv(next) })
  }

  const setRooms = (key: 'bedrooms' | 'bathrooms', value: number) => {
    setFilter({ [key]: value > 0 ? value : undefined })
  }

  // Price: local while dragging, committed at the end of the interaction. The
  // draft re-syncs whenever the APPLIED bounds change, which covers Start Over,
  // quick chips, and the meta range arriving after first paint.
  const appliedLo = vf.price_min ?? priceMin
  const appliedHi = vf.price_max ?? priceMax
  const [priceDraft, setPriceDraft] = useState<[number, number]>([appliedLo, appliedHi])
  useEffect(() => { setPriceDraft([appliedLo, appliedHi]) }, [appliedLo, appliedHi])
  const commitPrice = (lo: number, hi: number) => {
    setFilter({
      price_min: lo > priceMin ? lo : undefined,
      price_max: hi < priceMax ? hi : undefined,
    })
  }
  const buckets = usePriceDistribution(priceMin, priceMax)

  // Keyword: typed locally, committed on a pause (400ms) or on blur. Closing
  // the popover does NOT unmount this component, so a word typed just before
  // the close is carried by the surviving debounce timer — there is nothing
  // for an unmount teardown to rescue. (There used to be one; it only ever
  // fired on a resetKey remount, i.e. Start Over, where re-committing the
  // word the visitor just reset is exactly the wrong move.)
  const [keyword, setKeyword] = useState(vf.keywords ?? '')
  const pendingKeyword = useRef<string | null>(null)
  const appliedKeyword = vf.keywords ?? ''
  useEffect(() => { setKeyword(appliedKeyword) }, [appliedKeyword])
  const commitKeyword = (value: string) => {
    pendingKeyword.current = null
    setFilter({ keywords: value.trim() ? value : undefined })
  }
  useEffect(() => {
    if (pendingKeyword.current === null) return
    const t = setTimeout(() => {
      // Re-check at FIRE time, not only at schedule time: a blur between the
      // two already committed the value and nulled the pending edit, and an
      // unguarded timer would then commit an empty string over it.
      if (pendingKeyword.current === null) return
      commitKeyword(pendingKeyword.current)
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword])

  const clearAll = () => {
    // The keyword box is LOCAL state behind a debounce, so unsetting the
    // filter is not enough on its own: a word typed inside the 400ms window
    // was never applied, so `keywords` does not change, so the applied-value
    // effect never re-runs — the box keeps the word and the pending timer
    // commits it a beat after the clear. Drop the pending edit and the
    // displayed text here. Both the pane's "Clear all" and the closed pill's
    // X land on this function, so this covers both.
    pendingKeyword.current = null
    setKeyword('')
    setFilter({
      keywords: undefined, types: undefined, amenities: undefined, amenities_and: undefined,
      featured: undefined, bedrooms: undefined, bathrooms: undefined,
      price_min: undefined, price_max: undefined,
    })
    setDescribe('')
  }

  // ── Amenity "Show all N" ────────────────────────────────────────────────
  const primaryAmenitiesCount = options.primaryAmenitiesCount ?? 8
  const [showAllAmenities, setShowAllAmenities] = useState(false)
  const hasMoreAmenities = curatedAmenities.length > primaryAmenitiesCount
  const visibleAmenities = showAllAmenities || !hasMoreAmenities
    ? curatedAmenities
    : curatedAmenities.slice(0, primaryAmenitiesCount)

  // ── By name (dark until central#2885) ───────────────────────────────────
  const [nameQuery, setNameQuery] = useState('')
  const [debouncedName, setDebouncedName] = useState('')
  const navigateToProperty = usePropertyNavigate()
  const nameFinder = useNameFinder(feedId, {
    enabled: sb.what.byNameTab,
    showAllBeforeTyping: sb.nameFinder.showAllBeforeTyping,
    serverLookupThreshold: sb.nameFinder.serverLookupThreshold,
  }, effectiveOpen)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(nameQuery), sb.autocomplete.debounceMs)
    return () => clearTimeout(t)
  }, [nameQuery, sb.autocomplete.debounceMs])
  useEffect(() => {
    if (effectiveOpen && sb.what.byNameTab) nameFinder.search(debouncedName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName, effectiveOpen, nameFinder.available])

  // ── Tabs ────────────────────────────────────────────────────────────────
  const showDescribe = sb.what.aiConcierge
  const showByName = sb.what.byNameTab && nameFinder.available
  const tabs: Tab[] = [
    ...(showDescribe ? (['describe'] as Tab[]) : []),
    'filter',
    ...(showByName ? (['byname'] as Tab[]) : []),
  ]
  const initialTab: Tab = showDescribe ? 'describe' : 'filter'
  const [tab, setTab] = useState<Tab>(initialTab)
  // A tab can disappear under the visitor (the name probe resolves, the admin
  // flips a setting between renders) — never strand the pane on a dead tab.
  useEffect(() => {
    if (!tabs.includes(tab)) setTab(initialTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.join(','), tab])

  const tabLabel: Record<Tab, string> = { describe: 'Describe', filter: 'Filter', byname: 'By name' }

  // Tabs + panes — shared verbatim between the dialog and the sheet stage
  // body (the footer is dialog-only; the sheet's Clear-all/Search footer and
  // the header pill's FieldClear cover its two actions there). The panes ride
  // AnimatedHeightSwitch (#944): tab switches crossfade + height-morph
  // instead of snapping, and same-pane growth (amenity "Show all", async
  // sections) tweens too. The tab bar and dialog footer stay outside — pinned.
  const whatBody = (
    <>
        {tabs.length > 1 && (
          <div className="hfse-sbp-what-tabs" role="tablist" aria-label="Input mode">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`hfse-sbp-what-tab${tab === t ? ' hfse-sbp-what-tab-active' : ''}`}
                onClick={() => setTab(t)}
              >
                {/* Per-tab inline SVG before the label (#925, toolkit 1df0842):
                    sparkle=Describe, funnel=Filter, house=By name. */}
                <span className="hfse-sbp-what-tab-icon" aria-hidden="true">
                  {t === 'describe' && (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 3l1.9 5.6 5.6 1.9-5.6 1.9L11 18l-1.9-5.6-5.6-1.9 5.6-1.9L11 3z" /><path d="M18.5 14.5l1 2.9 2.9 1-2.9 1-1 2.9-1-2.9-2.9-1 2.9-1 1-2.9z" /></svg>
                  )}
                  {t === 'filter' && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16l-6.2 7.2V17l-3.6 2v-6.8L4 5z" /></svg>
                  )}
                  {t === 'byname' && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 10.5 12 3.5l8.5 7" /><path d="M5.5 9.5V20h4.6v-5.4h3.8V20h4.6V9.5" /></svg>
                  )}
                </span>
                {tabLabel[t]}
              </button>
            ))}
          </div>
        )}

        <AnimatedHeightSwitch switchKey={tab} className="hfse-sbp-what-panes">
        {tab === 'describe' && (
          <div className="hfse-sbp-what-pane hfse-sbp-what-describe" role="tabpanel">
            <div className="hfse-sbp-what-describe-box">
              <textarea
                className="hfse-sbp-what-describe-input"
                rows={3}
                placeholder={sb.what.describePlaceholder}
                aria-label="Describe what you are looking for"
                value={describe}
                onChange={(e) => setDescribe(e.target.value)}
              />
            </div>
            {sb.what.suggestedDescriptions.length > 0 && (
              <>
                <h4 className="hfse-sbp-what-suggestions-heading">Suggested descriptions</h4>
                <ul className="hfse-sbp-what-suggestions">
                  {sb.what.suggestedDescriptions.map((suggestion) => (
                    <li key={suggestion}>
                      <button type="button" className="hfse-sbp-what-suggestion" onClick={() => setDescribe(suggestion)}>
                        <span className="hfse-sbp-what-suggestion-icon" aria-hidden>{searchGlyph}</span>
                        <span className="hfse-sbp-what-suggestion-text">{suggestion}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {tab === 'filter' && (
          <div className="hfse-sbp-what-pane hfse-sbp-what-filter" role="tabpanel">
            {/* Sections render in the admin's saved order — enabledFilters is
                the ordered array behind the dashboard's drag-to-reorder
                control (r7 #936). Each branch keeps its own visibility guard
                (data-gated sections stay hidden even when enabled). */}
            {enabledFilters.map((section) => {
            if (section === 'keyword') {
            return visibility.keyword && (
              <div className="hfse-sbp-filter-section" key="keyword">
                {/* No section heading (r8 #937) — the placeholder carries the
                    label. Still "Filter", not "Search" (r7 #936): it narrows
                    the live results like every other section here — the
                    jump-to-property finder is the separate By-name tab. */}
                <div className="hfse-sbp-what-inputbox">
                  <span className="hfse-sbp-what-inputbox-icon" aria-hidden>{searchGlyph}</span>
                  <input
                    type="text"
                    className="hfse-sbp-what-inputbox-input"
                    placeholder="Filter by property name"
                    aria-label="Filter by property name"
                    value={keyword}
                    onChange={(e) => {
                      pendingKeyword.current = e.target.value
                      setKeyword(e.target.value)
                    }}
                    onBlur={() => { if (pendingKeyword.current !== null) commitKeyword(pendingKeyword.current) }}
                  />
                  {/* Value-driven, like every FieldClear (r8 #937). Clearing
                      goes through commitKeyword so the pending debounce edit
                      dies with the text — setKeyword alone would let a timer
                      scheduled mid-word re-apply it a beat later. */}
                  {keyword !== '' && (
                    <FieldClear label="Clear property name filter" onClear={() => { setKeyword(''); commitKeyword('') }} />
                  )}
                </div>
              </div>
            )
            }
            if (section === 'rooms') {
            return (visibility.bedrooms || visibility.bathrooms) && (
              <div className="hfse-sbp-filter-section" key="rooms">
                <h4 className="hfse-sbp-filter-section-title">Rooms and beds</h4>
                {visibility.bedrooms && (
                  <StepperRow label="Bedrooms" value={vf.bedrooms ?? 0} onChange={(v) => setRooms('bedrooms', v)} />
                )}
                {visibility.bathrooms && (
                  <StepperRow label="Bathrooms" value={vf.bathrooms ?? 0} onChange={(v) => setRooms('bathrooms', v)} />
                )}
              </div>
            )
            }
            if (section === 'types') {
            return visibility.types && (
              <div className="hfse-sbp-filter-section" key="types">
                <h4 className="hfse-sbp-filter-section-title">Property type</h4>
                <div className="hfse-sbp-filter-chips">
                  {curatedTypes.map((type) => {
                    const id = String(type.id)
                    const active = selectedTypes.includes(id)
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`hfse-sbp-filter-chip${active ? ' hfse-sbp-filter-chip-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => toggleType(id)}
                      >
                        {type.name}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
            }
            if (section === 'price') {
            return visibility.price && (
              <div className="hfse-sbp-filter-section hfse-sbp-filter-section-price" key="price">
                <h4 className="hfse-sbp-filter-section-title">Price range</h4>
                <p className="hfse-sbp-filter-section-subtitle">Nightly rate, before taxes &amp; fees</p>
                <PriceHistogramSlider
                  min={priceMin}
                  max={priceMax}
                  step={options.priceStep ?? 5}
                  valueMin={priceDraft[0]}
                  valueMax={priceDraft[1]}
                  buckets={buckets}
                  onChange={(lo, hi) => setPriceDraft([lo, hi])}
                  onCommit={commitPrice}
                />
              </div>
            )
            }
            return visibility.amenities && (
              <div className="hfse-sbp-filter-section" key="amenities">
                <h4 className="hfse-sbp-filter-section-title">Amenities</h4>
                <div className="hfse-sbp-filter-chips">
                  {visibleAmenities.map((amenity) => {
                    const id = String(amenity.id)
                    const active = selectedAmenities.includes(id)
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`hfse-sbp-filter-chip${active ? ' hfse-sbp-filter-chip-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => toggleAmenity(id)}
                      >
                        {amenity.name}
                      </button>
                    )
                  })}
                </div>
                {hasMoreAmenities && (
                  <button
                    type="button"
                    className="hfse-sbp-filter-show-more"
                    aria-expanded={showAllAmenities}
                    onClick={() => setShowAllAmenities((v) => !v)}
                  >
                    {showAllAmenities ? 'Show less' : `Show all ${curatedAmenities.length}`}
                  </button>
                )}
              </div>
            )
            })}
          </div>
        )}

        {tab === 'byname' && (
          <div className="hfse-sbp-what-pane hfse-sbp-what-byname" role="tabpanel">
            <div className="hfse-sbp-what-inputbox">
              <span className="hfse-sbp-what-inputbox-icon" aria-hidden>{searchGlyph}</span>
              <input
                type="text"
                className="hfse-sbp-what-inputbox-input"
                role="combobox"
                aria-expanded={nameFinder.matches.length > 0}
                aria-autocomplete="list"
                autoComplete="off"
                placeholder="Search by property name"
                aria-label="Search by property name"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
              />
              {/* nameQuery is plain local state (no debounce contract like the
                  keyword field) — clearing the text is the whole job. */}
              {nameQuery !== '' && (
                <FieldClear label="Clear property name search" onClear={() => setNameQuery('')} />
              )}
            </div>
            <ul className="hfse-sbp-what-byname-results" role="listbox" aria-label="Matching properties">
              {nameFinder.matches.map((m) => (
                <li key={m.slug}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="hfse-sbp-what-byname-result"
                    onClick={() => navigateToProperty(m.slug)}
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        </AnimatedHeightSwitch>

    </>
  )

  if (sheet) {
    return (
      <>
        <button
          type="button"
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-filters-field hfse-sbp-stage-header${fieldHasValue ? ' hfse-sbp-field-has-value' : ''}`}
          aria-expanded={sheetActive}
          onClick={onSheetActivate}
        >
          <span className="hfse-sbp-field-label">{labels.filtersLabel}</span>
          <span className={`hfse-sbp-field-value ${fieldHasValue ? '' : 'hfse-sbp-field-placeholder'}`}>
            {fieldValue}
          </span>
          {fieldHasValue && <FieldClear label="Clear filters" onClear={clearAll} />}
        </button>
        {sheetActive && (
          <div className="hfse-sbp-stage-body hfse-sbp-what-inline">
            <h3 className="hfse-sbp-stage-question">Anything else?</h3>
            {whatBody}
          </div>
        )}
      </>
    )
  }

  return (
    // measureNow at the open flip batches the anchor rect with the open state
    // — the portal's first paint is already positioned (When/Who pattern).
    <Dialog modal={!popover} open={open} onOpenChange={(o) => { if (o) measureNow(); setOpen(o) }}>
      <DialogTrigger asChild>
        <button
          type="button"
          ref={triggerRef}
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-filters-field${fieldHasValue ? ' hfse-sbp-field-has-value' : ''}`}
        >
          {/* #1700 compact: icon-only Filters button. The label and the value
              stay in the DOM, screen-reader only, so the accessible name is
              "Filters" plus the count; the clear below becomes the accent
              badge on the icon (CSS). */}
          {compactBar.compact
            ? <><FiltersIcon /><SrLabel>{labels.filtersLabel}</SrLabel></>
            : <span className="hfse-sbp-field-label">{labels.filtersLabel}</span>}
          <span className={`hfse-sbp-field-value ${fieldHasValue ? '' : 'hfse-sbp-field-placeholder'}`}>
            {fieldValue}
          </span>
          {/* Live-apply means the field's state IS the applied state, so the
              closed-pill clear reuses the pane's own clearAll. */}
          {fieldHasValue && <FieldClear label="Clear filters" onClear={clearAll} />}
        </button>
      </DialogTrigger>
      <DialogContent
        className={`hk-text-default hfse-form-modal hfse-sbp-what hfse-sbp-popover${anchored ? ' hfse-sbp-popover-anchored' : ''}`}
        overlayClassName={`hfse-sbp-popover-overlay${anchored ? ' hfse-sbp-popover-overlay-anchored' : ''}`}
        wrapperStyle={wrapperStyle}
        hideClose={anchored}
        // Pointer/Escape dismiss only — chained hand-offs must survive the
        // closing dialog's focus return (#923; see DateFilter).
        onFocusOutside={popover ? (e) => e.preventDefault() : undefined}
        elevateDom
        cssString={cssString}
        title="Filters"
      >
        {whatBody}
        <div className="hfse-sbp-what-footer">
          {activeCount > 0 && (
            <button type="button" className="hk-button hk-button-link hfse-modal-reset-button hfse-show-always" onClick={clearAll}>
              Clear all
            </button>
          )}
          <button
            type="button"
            className="hk-button hk-button-small hfse-sbp-what-close"
            style={{ marginLeft: 'auto' }}
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Toolkit stepper row: label on the left, −/count/+ on the right, "Any" at 0. */
function StepperRow({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <div className="hfse-sbp-stepper-row">
      <span className="hfse-sbp-stepper-label">{label}</span>
      <div className="hfse-sbp-stepper">
        <button
          type="button"
          className="hfse-sbp-stepper-btn"
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={value <= 0}
          onClick={() => onChange(Math.max(0, value - 1))}
        >
          &#8722;
        </button>
        <output className="hfse-sbp-stepper-count" aria-live="polite">{value > 0 ? value : 'Any'}</output>
        <button
          type="button"
          className="hfse-sbp-stepper-btn"
          aria-label={`Increase ${label.toLowerCase()}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  )
}
